import { fetchStock, assertStock, stockMessage, showCartMessage, guardCheckout } from './colet-stock.js?v=air-sea-20261027-2';

class ColetCartPage extends HTMLElement {
  connectedCallback() {
    this.handleCheckoutSubmit = (event) => guardCheckout(event, this);
    this.addEventListener('submit', this.handleCheckoutSubmit);
    this.handleClick = this.handleClick.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleResize = this.handleResize.bind(this);

    document.addEventListener('click', this.handleClick);
    document.addEventListener('cart:update', this.handleCartUpdate);
    window.addEventListener('resize', this.handleResize);
    this.scheduleOverflowUpdate();
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.handleCheckoutSubmit);
    document.removeEventListener('click', this.handleClick);
    document.removeEventListener('cart:update', this.handleCartUpdate);
    window.removeEventListener('resize', this.handleResize);
    window.cancelAnimationFrame(this.overflowFrame);
  }

  handleResize() {
    this.scheduleOverflowUpdate();
  }

  scheduleOverflowUpdate() {
    window.cancelAnimationFrame(this.overflowFrame);
    this.overflowFrame = window.requestAnimationFrame(() => this.updateOverflowState());
  }

  updateOverflowState() {
    const items = this.querySelector('[data-colet-cart-page-items]');
    const viewportHeight = Math.max(0, window.innerHeight);

    if (!(items instanceof HTMLElement) || window.matchMedia('(max-width: 700px)').matches) {
      this.dataset.overflowing = 'false';
      return;
    }

    const availableItemsHeight = Math.max(0, viewportHeight - 156);
    this.dataset.overflowing = String(items.scrollHeight > availableItemsHeight + 1);
  }

  handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const sizeMenu = target.closest('[data-colet-cart-page-size]');
    const sizeToggle = target.closest('.colet-cart-page__size-toggle');

    if (sizeToggle && this.contains(sizeToggle)) {
      this.closeSizeMenus(sizeMenu);
    } else if (!sizeMenu) {
      this.closeSizeMenus();
    }

    const action = target.closest('[data-colet-cart-page-action]');
    if (!(action instanceof HTMLElement) || !this.contains(action)) return;

    event.preventDefault();
    this.handleAction(action);
  }

  handleCartUpdate(event) {
    const source = event.detail?.data?.source;
    if (source === 'colet-cart-page') return;
    this.refreshSection();
  }

  closeSizeMenus(exceptMenu = null) {
    this.querySelectorAll('[data-colet-cart-page-size][open]').forEach((menu) => {
      if (menu !== exceptMenu && menu instanceof HTMLDetailsElement) {
        menu.open = false;
      }
    });
  }

  async handleAction(action) {
    if (this.hasAttribute('aria-busy')) return;

    const type = action.dataset.coletCartPageAction;
    const cartKey = action.dataset.cartKey;
    if (!cartKey) return;

    if (type === 'remove') {
      await this.changeLine(cartKey, 0);
      return;
    }

    if (type === 'quantity') {
      const quantity = Number(action.dataset.quantity);
      if (!Number.isFinite(quantity) || quantity < 0) return;
      await this.changeLine(cartKey, quantity);
      return;
    }

    if (type === 'variant') {
      const variantId = Number(action.dataset.variantId);
      const currentVariantId = Number(action.dataset.currentVariantId);
      const quantity = Number(action.dataset.quantity || 1);

      if (!variantId || variantId === currentVariantId) return;
      const storedProperties = JSON.parse(action.closest('[data-cart-properties]')?.dataset.cartProperties || '{}');
      const properties = Array.isArray(storedProperties) ? Object.fromEntries(storedProperties) : storedProperties;
      await this.changeVariant(cartKey, variantId, quantity, properties);
    }
  }

  async changeVariant(cartKey, variantId, quantity, properties) {
    this.setBusy(true);
    showCartMessage(this, '');

    try {
      assertStock(await fetchStock(), variantId, quantity);
      const response = await fetch(this.route('cart_add_url', '/cart/add.js'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity, properties }] }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'stock-unavailable');
      await this.changeLine(cartKey, 0, false);
    } catch (error) {
      showCartMessage(this, error.message?.startsWith('stock-') ? stockMessage(this, error) : error.message);
    } finally {
      this.setBusy(false);
    }
  }

  async changeLine(cartKey, quantity, manageBusy = true) {
    if (manageBusy) this.setBusy(true);
    const scrollTop = this.itemsScrollTop();

    showCartMessage(this, '');
    try {
      if (quantity > 0) {
        const stock = await fetchStock();
        const item = stock.items.find((entry) => entry.key === cartKey);
        if (!item) throw new Error('stock-unavailable');
        if (quantity > item.quantity) assertStock(stock, item.variantId, quantity, item.key);
      }
      const response = await fetch(this.route('cart_change_url', '/cart/change.js'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: cartKey,
          quantity,
          sections: this.dataset.sectionId,
          sections_url: window.location.pathname,
        }),
      });

      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update cart.');

      this.handleCartResponse(cart, scrollTop);
    } catch (error) {
      showCartMessage(this, error.message?.startsWith('stock-') ? stockMessage(this, error) : error.message);
    } finally {
      if (manageBusy) this.setBusy(false);
    }
  }

  handleCartResponse(cart, scrollTop = 0) {
    const renderedSection = cart.sections?.[this.dataset.sectionId];
    if (renderedSection) this.renderFromSection(renderedSection, scrollTop);

    if (Number.isFinite(cart.item_count)) this.updateCartCount(cart.item_count);

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          data: {
            itemCount: cart.item_count,
            sections: cart.sections,
            source: 'colet-cart-page',
          },
        },
      })
    );
  }

  async refreshSection() {
    if (!this.dataset.sectionId || this.dataset.refreshing === 'true') return;
    this.dataset.refreshing = 'true';

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', this.dataset.sectionId);
      url.searchParams.set('_colet_refresh', Date.now());

      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      const sections = await response.json();
      const renderedSection = sections?.[this.dataset.sectionId];

      if (renderedSection) this.renderFromSection(renderedSection, this.itemsScrollTop());
    } finally {
      this.dataset.refreshing = 'false';
    }
  }

  renderFromSection(sectionHtml, scrollTop = 0) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextPage = doc.querySelector('[data-colet-cart-page]');
    if (!nextPage) return;

    this.innerHTML = nextPage.innerHTML;
    this.dataset.state = nextPage.dataset.state || 'empty';
    this.dataset.cartCount = nextPage.dataset.cartCount || '0';

    window.requestAnimationFrame(() => {
      this.updateOverflowState();
      const items = this.querySelector('[data-colet-cart-page-items]');
      if (items instanceof HTMLElement && this.dataset.overflowing === 'true') {
        items.scrollTop = scrollTop;
      }
    });
  }

  itemsScrollTop() {
    const items = this.querySelector('[data-colet-cart-page-items]');
    return items instanceof HTMLElement ? items.scrollTop : 0;
  }

  updateCartCount(count) {
    const normalizedCount = Number.isFinite(count) ? count : 0;
    document.querySelectorAll('[data-colet-cart-count]').forEach((element) => {
      element.textContent = String(normalizedCount);
    });
    this.dataset.cartCount = String(normalizedCount);
  }

  setBusy(isBusy) {
    if (isBusy) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');
  }

  route(name, fallback) {
    const route = window.Theme?.routes?.[name] || fallback;
    if (name === 'cart_change_url' && !route.endsWith('.js')) return `${route}.js`;
    return route;
  }
}

if (!customElements.get('colet-cart-page')) {
  customElements.define('colet-cart-page', ColetCartPage);
}

